import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LIVE_LIST_OPTIONS } from '../../utils/liveList';
import { useLocation, useNavigate } from 'react-router-dom';
import { consultationService, facturationService } from '../../services/api';
import { Consultation, PaginatedResponse } from '@/types';
import { Search, Plus, Eye, FileText, Calendar, Trash2, Receipt, X, Stethoscope } from 'lucide-react';
import Pagination from '../common/Pagination';
import ConsultationForm from './ConsultationForm';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole } from '../../utils/roles';

// ── Quick invoice creation modal ─────────────────────────────────────────
interface CreateInvoiceModalProps {
  consultation: Consultation;
  onClose: () => void;
  onSuccess: () => void;
}

// Montant d'une ligne de traitement, robuste : montant_total si présent,
// sinon recalcul quantité × prix unitaire (évite tout NaN à l'affichage)
const ligneMontant = (t: { montant_total?: number | string; prix_unitaire?: number | string; quantite?: number }): number => {
  const direct = Number(t.montant_total);
  if (Number.isFinite(direct)) return direct;
  const calcule = Number(t.prix_unitaire) * Number(t.quantite ?? 1);
  return Number.isFinite(calcule) ? calcule : 0;
};

const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({ consultation, onClose, onSuccess }) => {
  const defaultTaux = Number(consultation.patient_details?.assureur_details?.taux_couverture) || 0;
  const [pourcentageAssurance, setPourcentageAssurance] = useState<number>(defaultTaux);
  const [dateEcheance, setDateEcheance] = useState<string>(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const today = new Date().toISOString().split('T')[0];
  const treatments = consultation.treatments ?? [];
  const montantActes = treatments.reduce((s, t) => s + ligneMontant(t), 0);
  const montantTotal = montantActes;
  const pct = Math.min(Math.max(pourcentageAssurance, 0), 100);
  const montantAssurance = Math.round((pct / 100) * montantTotal);
  const montantPatient = montantTotal - montantAssurance;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await facturationService.createFacture({
        patient: consultation.patient,
        consultation: consultation.id,
        date_emission: today,
        date_echeance: dateEcheance,
        montant_actes: montantActes,
        montant_supplementaire: 0,
        montant_total: montantTotal,
        pourcentage_remise: 0,
        montant_remise: 0,
        pourcentage_assurance: pct,
        montant_assurance: montantAssurance,
        montant_patient: montantPatient,
        notes,
        status: 'emise',
      } as any);
      onSuccess();
    } catch (err: any) {
      console.error(err);
      const data = err?.response?.data;
      const detail =
        (Array.isArray(data?.consultation) ? data.consultation[0] : data?.consultation) ||
        data?.detail ||
        'Erreur lors de la création de la facture';
      alert(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0D1F35]/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="create-invoice-title">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto border border-[color:var(--border)]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[color:var(--border)]">
          <div className="flex items-center gap-3">
            <div className="stat-icon-teal !h-10 !w-10"><Receipt className="h-5 w-5" aria-hidden /></div>
            <div>
            <h2 id="create-invoice-title" className="text-lg font-bold text-[color:var(--text)]">Créer une facture</h2>
            <p className="text-[13px] text-[color:var(--muted)] mt-0.5">
              {consultation.patient_details?.nom} {consultation.patient_details?.prenoms} —{' '}
              {new Date(consultation.date_consultation).toLocaleDateString('fr-FR')}
            </p>
            </div>
          </div>
          <button onClick={onClose} className="son-icon-btn" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Traitements */}
        <div className="px-6 pt-4 pb-2">
          <p className="son-field-label mb-2">Actes / Traitements</p>
          {treatments.length === 0 ? (
            <p className="alert-warning">
              Aucun traitement enregistré sur cette consultation — la facture sera créée sans lignes.
            </p>
          ) : (
            <div className="border border-[color:var(--border)] rounded-xl overflow-hidden">
              <table className="son-table w-full [&_td]:!py-2 [&_th]:!py-2">
                <thead>
                  <tr>
                    <th>Traitement</th>
                    <th className="!text-center">Qté</th>
                    <th className="!text-right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {treatments.map((t) => (
                    <tr key={t.id}>
                      <td>
                        {(t as any).treatment_details?.name ?? `Traitement #${t.treatment_type}`}
                      </td>
                      <td className="text-center tabular-nums">{t.quantite}</td>
                      <td className="text-right font-semibold tabular-nums whitespace-nowrap">
                        {ligneMontant(t).toLocaleString('fr-FR')} GNF
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[color:var(--panel-2)] border-t border-[color:var(--border)]">
                    <td colSpan={2} className="px-4 py-2 font-semibold text-[13px]">Total actes</td>
                    <td className="px-4 py-2 text-right font-extrabold tabular-nums whitespace-nowrap text-[13.5px]">
                      {montantActes.toLocaleString('fr-FR')} GNF
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Champs */}
        <div className="px-6 py-4 space-y-4">
          {/* Assurance */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label">
                Prise en charge assurance (%)
                {consultation.patient_details?.assureur_details && (
                  <span className="ml-1 text-xs text-[color:var(--blue)] font-normal">
                    — {consultation.patient_details.assureur_details.nom}
                  </span>
                )}
              </label>
              <input
                type="number"
                min={0}
                max={100}
                className="form-input text-gray-700"
                value={pourcentageAssurance}
                onChange={e => setPourcentageAssurance(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="form-label">Date d'échéance</label>
              <input
                type="date"
                className="form-input text-gray-700"
                value={dateEcheance}
                onChange={e => setDateEcheance(e.target.value)}
              />
            </div>
          </div>

          {/* Récapitulatif financier */}
          <div className="rounded-xl px-4 py-3 space-y-1.5 text-sm bg-[color:var(--panel-2)] border border-[color:var(--border-soft)] tabular-nums">
            <div className="flex justify-between text-gray-700">
              <span>Total</span>
              <span className="font-semibold">{montantTotal.toLocaleString('fr-FR')} GNF</span>
            </div>
            {pct > 0 && (
              <div className="flex justify-between text-[color:var(--blue)]">
                <span>Part assurance ({pct}%)</span>
                <span className="font-semibold">- {montantAssurance.toLocaleString('fr-FR')} GNF</span>
              </div>
            )}
            <div className="flex justify-between text-[color:var(--text)] font-extrabold border-t border-[color:var(--border)] pt-1.5 mt-1 text-[15px]">
              <span>Part patient</span>
              <span>{montantPatient.toLocaleString('fr-FR')} GNF</span>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="form-label">Notes (optionnel)</label>
            <textarea
              rows={2}
              className="form-input text-gray-700"
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[color:var(--border)] flex justify-end gap-3 bg-[color:var(--panel-2)] rounded-b-2xl">
          <button
            onClick={onClose}
            className="btn-secondary text-sm"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="btn-primary text-sm"
          >
            {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
            <Receipt className="h-4 w-4" />
            Créer la facture
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ConsultationList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [billingFilter, setBillingFilter] = useState<string>(''); // 'billed', 'unbilled', ''
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [invoiceTarget, setInvoiceTarget] = useState<Consultation | null>(null);
  const pageSize = 25;
  const location = useLocation();

  // Fonction pour obtenir les dates selon le filtre rapide
  const getDateRange = (filter: string): { start: string; end: string } | null => {
    const today = new Date();
    const start = new Date();
    const end = new Date();

    switch (filter) {
      case 'today':
        return {
          start: today.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        };
      case 'yesterday':
        start.setDate(today.getDate() - 1);
        return {
          start: start.toISOString().split('T')[0],
          end: start.toISOString().split('T')[0]
        };
      case 'week':
        start.setDate(today.getDate() - 7);
        return {
          start: start.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        };
      case 'month':
        start.setMonth(today.getMonth() - 1);
        return {
          start: start.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        };
      case 'custom':
        if (startDate && endDate) {
          return { start: startDate, end: endDate };
        }
        return null;
      default:
        return null;
    }
  };

  const dateRange = getDateRange(dateFilter);

  const { data, isLoading, refetch } = useQuery<PaginatedResponse<Consultation>>(
    ['consultations', { search: searchTerm, dateFilter, startDate, endDate, billingFilter, page: currentPage }],
    () => consultationService.getAll({
      search: searchTerm,
      page: currentPage,
      page_size: pageSize,
      ordering: '-date_consultation', // Tri par date décroissante (plus récent en premier)
      ...(dateRange && { date_debut: dateRange.start, date_fin: dateRange.end }),
      // Filtré côté serveur : appliqué à la seule page chargée, il renvoyait 0
      // dès que les 25 consultations les plus récentes étaient facturées
      ...(billingFilter === 'unbilled' && { facturee: false }),
      ...(billingFilter === 'billed' && { facturee: true }),
    }).then(res => res.data),
    LIVE_LIST_OPTIONS
  );

  const consultations = data?.results || [];

  // Réinitialiser la page à 1 quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, startDate, endDate, billingFilter]);

  // Détecter si on vient d'un RDV pour ouvrir automatiquement le formulaire
  useEffect(() => {
    const navigationState = location.state as { 
      patientId?: number; 
      doctorId?: number; 
      rendezVousId?: number; 
      motif?: string;
      patientInfo?: any;
    } | null;

    if (navigationState && (navigationState.patientId || navigationState.doctorId)) {
      setShowForm(true);
      // Réinitialiser l'état de navigation pour éviter la réouverture
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleEdit = (consultation: Consultation): void => {
    setSelectedConsultation(consultation);
    setShowForm(true);
  };

  const handleCloseForm = (): void => {
    setShowForm(false);
    setSelectedConsultation(null);
  };

  const handleSuccess = (): void => {
    refetch();
    handleCloseForm();
  };

  const handleDelete = async (consultation: Consultation): Promise<void> => {
    const confirmMessage = `Êtes-vous sûr de vouloir supprimer la consultation du ${new Date(consultation.date_consultation).toLocaleDateString('fr-FR')} pour ${consultation.patient_details?.nom || 'ce patient'} ?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await consultationService.deleteConsultation(consultation.id);
      refetch();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de la consultation');
    }
  };

  const handlePrintOrdonnance = async (consultation: Consultation): Promise<void> => {
    try {
      const response = await consultationService.imprimerOrdonnance(consultation.id);

      // Créer un blob à partir de la réponse
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Créer une URL pour le blob
      const url = window.URL.createObjectURL(blob);

      // Ouvrir le PDF dans un nouvel onglet
      window.open(url, '_blank');

      // Libérer l'URL après un court délai
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Erreur lors de l\'impression de l\'ordonnance:', error);
      alert('Erreur lors de l\'impression de l\'ordonnance');
    }
  };

  if (showForm) {
    return (
      <ConsultationForm
        consultation={selectedConsultation || undefined}
        onClose={handleCloseForm}
        onSuccess={handleSuccess}
      />
    );
  }

  const hasFilters = Boolean(dateFilter || searchTerm || billingFilter);
  const resetFilters = (): void => {
    setSearchTerm('');
    setDateFilter('');
    setStartDate('');
    setEndDate('');
    setBillingFilter('');
  };

  const doctorName = (c: Consultation): string => {
    const u = c.doctor_details?.user_details ?? c.doctor_details?.user;
    return u ? `Dr. ${u.first_name} ${u.last_name}` : '-';
  };

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="son-page-head">
        <div>
          <h1 className="son-h1">Consultations</h1>
          <p className="son-sub">
            {data && `${data.count} consultation${data.count > 1 ? 's' : ''} · `}Triées de la plus récente à la plus ancienne
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary">
          <Plus className="h-4 w-4" aria-hidden />
          Nouvelle consultation
        </button>
      </div>

      {/* Recherche & filtres */}
      <div className="son-toolbar">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
          <div className="son-search">
            <label htmlFor="consultSearch" className="sr-only">Rechercher une consultation</label>
            <Search aria-hidden />
            <input
              id="consultSearch"
              type="search"
              placeholder="Patient, motif, diagnostic…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input text-gray-700"
            />
          </div>

          <div className="lg:w-56">
            <label htmlFor="dateFilter" className="son-field-label">
              <Calendar className="inline h-3.5 w-3.5 mr-1 -mt-0.5" aria-hidden />
              Période
            </label>
            <select
              id="dateFilter"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                if (e.target.value !== 'custom') {
                  setStartDate('');
                  setEndDate('');
                }
              }}
              className="form-select text-gray-700"
            >
              <option value="">Toutes les dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="yesterday">Hier</option>
              <option value="week">7 derniers jours</option>
              <option value="month">30 derniers jours</option>
              <option value="custom">Période personnalisée</option>
            </select>
          </div>

          {dateFilter === 'custom' && (
            <>
              <div className="lg:w-44">
                <label htmlFor="startDate" className="son-field-label">Date début</label>
                <input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-input text-gray-700"
                />
              </div>
              <div className="lg:w-44">
                <label htmlFor="endDate" className="son-field-label">Date fin</label>
                <input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-input text-gray-700"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="son-seg" role="group" aria-label="Statut de facturation">
            {[
              { value: '', label: 'Toutes' },
              { value: 'unbilled', label: 'Non facturées' },
              { value: 'billed', label: 'Facturées' },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                aria-pressed={billingFilter === opt.value}
                onClick={() => setBillingFilter(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {hasFilters && (
            <button onClick={resetFilters} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[color:var(--muted)] hover:text-[color:var(--text)] px-2 py-1.5 rounded-lg">
              <X className="h-3.5 w-3.5" aria-hidden />
              Réinitialiser les filtres
            </button>
          )}
        </div>
      </div>

      {/* Liste des consultations */}
      <div className="son-table-wrap" aria-busy={isLoading}>
        <div className="overflow-x-auto">
          <table className="son-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Patient</th>
                <th>Praticien</th>
                <th>Motif · diagnostic</th>
                <th>Facturation</th>
                <th className="!text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {[24, 40, 28, 48, 20, 16].map((w, j) => (
                        <td key={j}><div className="son-skeleton h-3.5" style={{ width: `${w * 3}px`, maxWidth: '100%' }} /></td>
                      ))}
                    </tr>
                  ))
                : consultations.map((consultation) => {
                    const d = new Date(consultation.date_consultation);
                    const nom = `${consultation.patient_details?.nom ?? ''} ${consultation.patient_details?.prenoms ?? ''}`.trim();
                    const initiales = `${consultation.patient_details?.nom?.[0] ?? ''}${consultation.patient_details?.prenoms?.[0] ?? ''}`.toUpperCase();
                    return (
                      <tr key={consultation.id}>
                        <td className="whitespace-nowrap">
                          <div className="font-semibold tabular-nums">{d.toLocaleDateString('fr-FR')}</div>
                          <div className="text-[11.5px] text-[color:var(--muted)] capitalize">
                            {d.toLocaleDateString('fr-FR', { weekday: 'long' })}
                          </div>
                        </td>
                        <td className="whitespace-nowrap">
                          <button
                            type="button"
                            className="flex items-center gap-2.5 text-left group"
                            onClick={() => navigate(`/patients/${consultation.patient}`)}
                            title="Voir le dossier patient"
                          >
                            <span className="son-avatar" aria-hidden>{initiales || '?'}</span>
                            <span className="font-semibold group-hover:text-[color:var(--blue)] group-hover:underline">
                              {nom || '—'}
                            </span>
                          </button>
                        </td>
                        <td className="whitespace-nowrap text-[color:var(--muted)]">{doctorName(consultation)}</td>
                        <td>
                          <div className="max-w-xs truncate font-medium" title={consultation.motif_consultation}>
                            {consultation.motif_consultation}
                          </div>
                          <div className="max-w-xs truncate text-[12px] text-[color:var(--muted)]" title={consultation.diagnostic || undefined}>
                            {consultation.diagnostic || 'Pas de diagnostic'}
                          </div>
                        </td>
                        <td className="whitespace-nowrap">
                          {consultation.facture ? (
                            <span className="son-chip son-chip-green">Facturée</span>
                          ) : consultation.sans_facturation ? (
                            <span className="son-chip son-chip-gray" title="Suite de soins clôturée sans facture">Sans facturation</span>
                          ) : (
                            <span className="son-chip son-chip-amber">Non facturée</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => handleEdit(consultation)}
                              className="son-icon-btn is-blue"
                              title="Voir/Modifier"
                              aria-label="Voir ou modifier la consultation"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {!consultation.facture && !consultation.sans_facturation && (
                              <button
                                onClick={() => setInvoiceTarget(consultation)}
                                className="son-icon-btn is-teal"
                                title="Créer une facture"
                                aria-label="Créer une facture"
                              >
                                <Receipt className="h-4 w-4" />
                              </button>
                            )}
                            {consultation.ordonnances && consultation.ordonnances.length > 0 && (
                              <button
                                onClick={() => handlePrintOrdonnance(consultation)}
                                className="son-icon-btn is-purple"
                                title="Imprimer l'ordonnance"
                                aria-label="Imprimer l'ordonnance"
                              >
                                <FileText className="h-4 w-4" />
                              </button>
                            )}
                            {hasRole(user, 'admin') && (
                              <button
                                onClick={() => handleDelete(consultation)}
                                className="son-icon-btn is-red"
                                title="Supprimer"
                                aria-label="Supprimer la consultation"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>

        {!isLoading && consultations.length === 0 && (
          <div className="son-empty">
            <div className="son-empty-icon"><Stethoscope className="h-6 w-6" aria-hidden /></div>
            <p className="font-semibold text-[color:var(--text)]">Aucune consultation trouvée</p>
            <p>{hasFilters ? 'Aucun résultat pour ces filtres.' : 'Les consultations enregistrées apparaîtront ici.'}</p>
            {hasFilters && (
              <button onClick={resetFilters} className="btn-secondary mt-2 text-sm">Réinitialiser les filtres</button>
            )}
          </div>
        )}

        {!isLoading && data && (
          <Pagination page={currentPage} pageSize={pageSize} count={data.count} label="consultations" onChange={setCurrentPage} />
        )}
      </div>

      {/* Quick invoice creation modal */}
      {invoiceTarget && (
        <CreateInvoiceModal
          consultation={invoiceTarget}
          onClose={() => setInvoiceTarget(null)}
          onSuccess={() => {
            setInvoiceTarget(null);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default ConsultationList;